<template>
  <div>
    <AuthViewIntro :title="$t('profile.forgot-pass.create-new-password')" />

    <AuthAlert v-if="errorMessage" :message="errorMessage" />

    <AuthTextField
      :value="password"
      type="password"
      autocomplete="new-password"
      :label="$t('general.new-password')"
      :error="passwordError"
      @input="$emit('update:password', $event)"
      @blur="touched.password = true" />

    <div v-if="password" class="new-password__strength">
      <span
        v-for="level in STRENGTH_LEVELS"
        :key="level"
        class="new-password__bar"
        :class="{ 'is-on': strength >= level }"
        :data-strength="strength"></span>
      <span class="new-password__strength-label">{{ strengthLabel }}</span>
    </div>

    <AuthTextField
      :value="confirmPassword"
      type="password"
      autocomplete="new-password"
      :label="$t('general.confirm-new-password')"
      :error="confirmError"
      @input="$emit('update:confirmPassword', $event)"
      @blur="touched.confirm = true"
      @enter="submit" />

    <AuthSubmitButton :disabled="!isValid" :loading="loading" @click="submit">
      {{ $t('profile.forgot-pass.create-new-password') }}
    </AuthSubmitButton>
  </div>
</template>

<script>
  import AuthViewIntro from '../../AuthViewIntro.vue';
  import AuthTextField from '../../AuthTextField.vue';
  import AuthSubmitButton from '../../AuthSubmitButton.vue';
  import AuthAlert from '../../AuthAlert.vue';

  const MIN_PASSWORD_LENGTH = 8;
  const STRENGTH_LEVELS = [1, 2, 3];

  export default {
    name: 'NewPasswordStep',
    components: {
      AuthViewIntro,
      AuthTextField,
      AuthSubmitButton,
      AuthAlert
    },
    props: {
      password: {
        type: String,
        default: ''
      },
      confirmPassword: {
        type: String,
        default: ''
      },
      loading: {
        type: Boolean,
        default: false
      },
      errorMessage: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        STRENGTH_LEVELS: STRENGTH_LEVELS,
        touched: {
          password: false,
          confirm: false
        }
      }
    },
    computed: {
      strength() {
        const value = this.password;
        if (!value) return 0;

        let score = value.length >= MIN_PASSWORD_LENGTH ? 1 : 0;
        if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
        if (/\d/.test(value) && /[^A-Za-z0-9]/.test(value)) score += 1;
        return score;
      },
      strengthLabel() {
        if (this.strength >= 3) return this.$t('profile.auth.strength-strong');
        if (this.strength === 2) return this.$t('profile.auth.strength-medium');
        return this.$t('profile.auth.strength-weak');
      },
      passwordError() {
        if (!this.touched.password) return '';
        if (!this.password) return this.$t('profile.auth.required-field');
        if (this.password.length < MIN_PASSWORD_LENGTH) return this.$t('profile.auth.min-password');
        return '';
      },
      confirmError() {
        if (!this.touched.confirm) return '';
        if (!this.confirmPassword) return this.$t('profile.auth.required-field');
        if (this.confirmPassword !== this.password) return this.$t('profile.auth.password-mismatch');
        return '';
      },
      isValid() {
        return this.password.length >= MIN_PASSWORD_LENGTH && this.password === this.confirmPassword;
      }
    },
    methods: {
      submit() {
        if (!this.isValid || this.loading) return;
        this.$emit('submit');
      }
    }
  }
</script>

<style scoped>
.new-password__strength {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: -8px 0 16px;
}
.new-password__bar {
  flex: 1;
  height: 4px;
  background: #E0E8EF;
  border-radius: 999px;
}
.new-password__bar.is-on[data-strength="1"] {
  background: #F04438;
}
.new-password__bar.is-on[data-strength="2"] {
  background: #F79009;
}
.new-password__bar.is-on[data-strength="3"] {
  background: #12B76A;
}
.new-password__strength-label {
  min-width: 56px;
  font-size: 0.6875rem;
  color: #6B7886;
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/components/v2/auth/views/forgot-password/NewPasswordStep.vue